/**
 * @module components/EditStatistics
 * @description 表格统计值配置（ChartSettingsV2）
 *
 * 状态管理模式：
 *   读/写: useSettingObjectState('statistics') + normalizeStatistics
 *
 * 保留 useUpdateEffect + 本地 state，避免首次渲染误同步（与旧版注释一致）
 */

import React, { useMemo, useState, useCallback } from 'react';
import { useUpdateEffect } from 'ahooks';
import classNames from 'classnames';
import { shallowEqual } from 'umi';
import { useDblIntl } from '@/pages/datalab/utils/utils';
import { getLanguage } from '@home/utils/common';
import TagFilterSelect from '@/pages/datalab/components/TagFilterSelect';
import type { IColumnInfo } from '@dbl/core/dataSource';
import { ChartCalculateMode, type TChartCalculateMode } from '@dbl/core/chartEditor';
import ConfigCollapseCard from '@/pages/datalab/components/ConfigCollapseCard';
import { useSettingObjectState } from '../../context';
import { useQuerySelector } from '../../hooks/deps';
import {
  hasStatisticsSelection,
  normalizeStatistics,
  STATISTICS_KEYS,
  type IStatisticsValue,
  type TStatisticsKey,
} from '../../types/settings/statistics';
import { EMPTY_COLUMN_INFO_LIST } from '../../utils/constants';
import './index.scss';

interface IMetricOption {
  text: string;
  value: string;
}

const NUMERIC_COLUMN_TYPES = ['number', 'float', 'percent'];

const EditStatistics: React.FC = () => {
  const { dblFormatMessage, isEn, locale } = useDblIntl('chart');
  const { value: storedValue, setValue: persistStatistics } =
    useSettingObjectState('statistics');
  const columnMetrics = useQuerySelector(
    s => s.columnMetrics ?? EMPTY_COLUMN_INFO_LIST,
  );
  const mode = useQuerySelector(s => s.mode as TChartCalculateMode);

  const columnRawColumns = useQuerySelector(
    s => (s.columnRawColumns as IColumnInfo[]) ?? [],
    shallowEqual,
  );

  const initValue = useMemo(
    () => normalizeStatistics(storedValue),
    [storedValue],
  );

  const [statistics, setStatistics] = useState<IStatisticsValue>(initValue);

  const metrics = useMemo((): IColumnInfo[] => {
    const detailColumnMetrics = columnRawColumns?.filter(
      item =>
        item?.is_metric ||
        (item?.column_custom &&
          NUMERIC_COLUMN_TYPES.includes(String(item?.column_type))),
    );
    return mode === ChartCalculateMode.RawColumn
      ? detailColumnMetrics || []
      : ((columnMetrics || []) as IColumnInfo[]);
  }, [columnMetrics, columnRawColumns, mode]);

  useUpdateEffect(() => {
    setStatistics(initValue);
  }, [JSON.stringify(initValue)]);

  // 操作区指标变更时，过滤无效统计项（对齐旧 ChartsDrop）
  useUpdateEffect(() => {
    if (!hasStatisticsSelection(statistics)) {
      return;
    }
    const metricsValue = metrics?.map(item => item?.column_name);
    if (!metricsValue?.length) {
      return;
    }
    const merged: IStatisticsValue = { ...statistics };
    let changed = false;
    STATISTICS_KEYS.forEach(key => {
      const values = statistics[key];
      if (!values?.length) {
        return;
      }
      const filtered = values.filter(v => metricsValue.includes(v));
      if (filtered.length !== values.length) {
        merged[key] = filtered;
        changed = true;
      }
    });
    if (!changed) {
      return;
    }
    setStatistics(merged);
    persistStatistics(merged);
  }, [JSON.stringify(metrics), JSON.stringify(statistics)]);

  const options = useMemo((): IMetricOption[] => {
    return (metrics || []).map(item => ({
      text: getLanguage(isEn, item?.column_display_en, item?.column_display),
      value: item?.column_name,
    }));
  }, [isEn, metrics]);

  const handleChange = useCallback(
    (key: TStatisticsKey) => (val: string[]) => {
      const next: IStatisticsValue = { ...statistics, [key]: val };
      setStatistics(next);
      persistStatistics(next);
    },
    [statistics, persistStatistics],
  );

  return (
    <ConfigCollapseCard
      title={dblFormatMessage('statistics')}
      className="dbl-chart-statistics"
      defaultActiveIds={[]}
    >
      <div className={classNames('dbl-chart-statistics-content')}>
        {STATISTICS_KEYS.map(key => (
          <TagFilterSelect
            className={`dbl-chart-statistics-content-select-${key}`}
            key={key}
            locale={locale}
            value={statistics[key]}
            name={dblFormatMessage('metric')}
            placeholder={dblFormatMessage('statistics_placeholder')}
            tooltip={dblFormatMessage(`statistics_${key}_tooltip`)}
            buttonText={dblFormatMessage(`statistics_${key}`)}
            options={options}
            listWidth={302}
            listHeight={180}
            searchable={false}
            bubblePlacement="left"
            onChange={handleChange(key)}
          />
        ))}
      </div>
    </ConfigCollapseCard>
  );
};

export default React.memo(EditStatistics);
